'use client';
import { useClientOnlyMediaQuery } from '~/common/hooks/use-client-only-media-query';
import { useVipStateWithCache } from '~/common/hooks/use-vip-state-with-cache';
import type { IMobileAdUnitInfo } from '~/utils/i-mobile-constants';
import { Media } from '~/utils/styles/enums';
import { css } from '@styled-system/css';
import { type CenterProps, Center } from '@styled-system/jsx';
import { useInitAd } from './use-init-ad';

type AdSize = {
  width: number;
  height: number;
};

type EstimateSize = Partial<{
  sp: AdSize | undefined;
  pc: AdSize | undefined;
}>;

type Props = {
  spAdUnit: IMobileAdUnitInfo | undefined;
  pcAdUnit: IMobileAdUnitInfo | undefined;
  estimateSize?: EstimateSize;
} & CenterProps;

export const IMobileRectangleContainer = ({
  spAdUnit,
  pcAdUnit,
  estimateSize,
  ...centerProps
}: Props) => {
  const screenLarge = useClientOnlyMediaQuery(Media.LARGE);
  const isMobile =
    typeof screenLarge === 'undefined' ? undefined : !screenLarge;
  const { storedVipState } = useVipStateWithCache();
  const adUnitInfo = isMobile ? spAdUnit : pcAdUnit;
  const adSize = isMobile ? estimateSize?.sp : estimateSize?.pc;

  // 指定の画面サイズの広告枠がないとき表示しない
  if (typeof isMobile === 'undefined')
    return (
      <Center
        w="100%"
        className={css({
          '& div:only-child': {
            my: '16px',
          },
        })}
        style={{
          minWidth: estimateSize?.sp?.width ?? estimateSize?.pc?.width,
          minHeight: estimateSize?.sp?.height ?? estimateSize?.pc?.height,
        }}
        {...centerProps}
      />
    );

  // 画面サイズが取得済みで広告枠がundefinedのとき、広告を表示しない
  if (storedVipState || !adUnitInfo) return null;

  return <AdInners adUnitInfo={adUnitInfo} adSize={adSize} {...centerProps} />;
};

const AdInners = ({
  adUnitInfo,
  adSize,
  ...centerProps
}: {
  adUnitInfo: IMobileAdUnitInfo;
  adSize: AdSize | undefined;
} & CenterProps) => {
  useInitAd(adUnitInfo);
  return (
    <Center
      id={adUnitInfo.elementid}
      w="100%"
      className={css({
        '& ins:only-child': {
          my: '16px',
          width: '100%',
        },
      })}
      style={{
        minWidth: adSize?.width,
        minHeight: adSize?.height,
      }}
      {...centerProps}
    />
  );
};
